<template>
    <v-container :fluid="true" class="max-height driver-details">
        <!-- Back + loading -->
        <v-row class="mb-2 px-3">
            <v-col cols="12" class="d-flex align-center ga-2">
                <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="goBack">Drivers</v-btn>
            </v-col>
        </v-row>

        <v-row v-if="loading" justify="center" class="py-12">
            <v-progress-circular indeterminate color="primary" size="48"/>
        </v-row>

        <v-alert v-else-if="!detail" type="error" variant="tonal" class="mx-3">
            Driver could not be loaded.
        </v-alert>

        <template v-else>
            <!-- Header -->
            <v-row class="px-3">
                <v-col cols="12">
                    <v-card rounded="xl" elevation="2" class="pa-4">
                        <div class="d-flex flex-wrap align-center ga-4">
                            <UserAvatar :src="avatarSrc" :first-name="driver.user?.first_name"
                                        :last-name="driver.user?.last_name" :email="driver.user?.email" size="72"/>
                            <div class="flex-grow-1">
                                <div class="d-flex align-center ga-2 flex-wrap">
                                    <h1 class="text-h5 font-weight-bold">{{ fullName || 'Driver' }}</h1>
                                    <v-chip :color="driver.status ? 'primary' : 'grey'" size="small" variant="tonal">
                                        {{ driver.status ? 'Active' : 'Inactive' }}
                                    </v-chip>
                                    <v-chip v-if="driver.current_status" size="small" color="success" variant="tonal"
                                            prepend-icon="mdi-steering">Driving</v-chip>
                                    <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-pencil"
                                           @click="editDialog = true">Edit</v-btn>
                                    <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-cellphone-message"
                                           :loading="inviting" @click="inviteToApp">Invite to app</v-btn>
                                </div>
                                <div class="text-body-2 text-grey-darken-1 mt-1">
                                    {{ driver.department || 'No department' }}
                                    <span v-if="driver.user?.email"> &middot; {{ driver.user.email }}</span>
                                </div>
                            </div>

                            <!-- Score badge -->
                            <div class="text-center px-4">
                                <div class="text-caption text-grey-darken-1">Safety score</div>
                                <div class="text-h4 font-weight-bold" :class="`text-${scoreColor(driver.driver_score)}`">
                                    {{ driver.driver_score != null ? Number(driver.driver_score).toFixed(1) : '--' }}
                                </div>
                                <v-chip :color="scoreColor(driver.driver_score)" size="x-small" variant="flat">
                                    {{ scoreBand(driver.driver_score) }}
                                </v-chip>
                                <div v-if="scoreTrend" class="d-flex align-center justify-center ga-1 mt-1"
                                     :class="`text-${scoreTrend.color}`">
                                    <v-icon size="16">{{ scoreTrend.icon }}</v-icon>
                                    <span class="text-caption">{{ scoreTrend.label }}</span>
                                    <v-tooltip activator="parent" location="bottom">vs ~30 days ago</v-tooltip>
                                </div>
                            </div>

                            <!-- Rank -->
                            <div v-if="detail.rank" class="text-center px-4">
                                <div class="text-caption text-grey-darken-1">Fleet rank</div>
                                <div class="text-h4 font-weight-bold">#{{ detail.rank }}</div>
                                <div class="text-caption text-grey">of {{ detail.total_drivers }}</div>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Tabs -->
            <v-row class="px-3 mt-1">
                <v-col cols="12">
                    <v-tabs v-model="tab" color="primary" density="comfortable">
                        <v-tab value="overview" prepend-icon="mdi-account-details">Overview</v-tab>
                        <v-tab value="analytics" prepend-icon="mdi-chart-line">Analytics</v-tab>
                        <v-tab value="compliance">
                            <v-icon start :color="complianceTabColor">{{ complianceTabIcon }}</v-icon>
                            Compliance
                        </v-tab>
                        <v-tab value="coaching" prepend-icon="mdi-school">Coaching</v-tab>
                        <v-tab value="vehicles" prepend-icon="mdi-car-multiple">Vehicles</v-tab>
                        <v-tab value="documents" prepend-icon="mdi-file-document-multiple">Documents</v-tab>
                        <v-tab value="trips" prepend-icon="mdi-map-marker-path">Trips</v-tab>
                        <v-tab value="shifts">
                            <v-icon start>mdi-clock-outline</v-icon>
                            Shifts
                            <v-badge v-if="shiftReviewCount" :content="shiftReviewCount" color="error" inline/>
                        </v-tab>
                        <v-tab value="apps" prepend-icon="mdi-cellphone">Mobile apps</v-tab>
                        <v-tab value="chat" prepend-icon="mdi-message-text">Chat</v-tab>
                    </v-tabs>

                    <v-window v-model="tab" class="mt-3">
                        <!-- OVERVIEW -->
                        <v-window-item value="overview">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                        <div class="text-subtitle-1 font-weight-bold mb-3">Contact</div>
                                        <DetailRow icon="mdi-email-outline" label="Email" :value="driver.user?.email"/>
                                        <DetailRow icon="mdi-phone-outline" label="Phone"
                                                   :value="driver.user?.contact_phone || driver.user?.mobile_phone"/>
                                        <div v-if="driver.user?.contact_phone || driver.user?.mobile_phone"
                                             class="d-flex ga-2 mt-1 mb-2">
                                            <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-phone"
                                                   :href="`tel:${cleanPhone(driver.user?.contact_phone || driver.user?.mobile_phone)}`">
                                                Call
                                            </v-btn>
                                            <v-btn size="small" variant="tonal" color="primary"
                                                   prepend-icon="mdi-message-text"
                                                   :href="`sms:${cleanPhone(driver.user?.contact_phone || driver.user?.mobile_phone)}`">
                                                Text
                                            </v-btn>
                                        </div>
                                        <DetailRow icon="mdi-clock-outline" label="Timezone" :value="driver.user?.timezone"/>
                                        <DetailRow icon="mdi-office-building-outline" label="Department"
                                                   :value="driver.department"/>
                                        <DetailRow icon="mdi-nfc-variant" label="RFID / Tag" :value="driver.rfid"/>
                                        <DetailRow icon="mdi-access-point" label="Device" :value="driver.device?.slno"/>
                                    </v-card>
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                        <div class="text-subtitle-1 font-weight-bold mb-3">License</div>
                                        <DetailRow icon="mdi-card-account-details" label="Number"
                                                   :value="driver.license_number"/>
                                        <DetailRow icon="mdi-shield-account" label="Class" :value="driver.license_class"/>
                                        <DetailRow icon="mdi-map-marker" label="State" :value="driver.license_state"/>
                                        <div class="d-flex align-center ga-2 py-1">
                                            <v-icon size="18" color="grey-darken-1">mdi-calendar-alert</v-icon>
                                            <span class="text-body-2 text-grey-darken-1" style="min-width: 110px">Expiry</span>
                                            <span class="text-body-2 font-weight-medium">
                                                {{ fmtDate(driver.license_expiry) }}
                                            </span>
                                            <v-chip v-if="licenseExpiringSoon" size="x-small" color="warning"
                                                    variant="tonal" class="ml-1">Expiring soon</v-chip>
                                        </div>
                                    </v-card>
                                </v-col>

                                <v-col cols="12">
                                    <v-card rounded="lg" variant="outlined" class="pa-4">
                                        <div class="text-subtitle-1 font-weight-bold mb-3">Assigned vehicles</div>
                                        <div v-if="detail.assets?.length" class="d-flex flex-wrap ga-2">
                                            <v-chip v-for="a in detail.assets" :key="a.id" color="primary"
                                                    variant="tonal" prepend-icon="mdi-car"
                                                    @click="openAsset(a.id)" style="cursor: pointer;">
                                                {{ a.label }}
                                            </v-chip>
                                        </div>
                                        <div v-else class="text-body-2 text-grey">No vehicles assigned.</div>
                                    </v-card>
                                </v-col>

                                <v-col v-if="driver.notes" cols="12">
                                    <v-card rounded="lg" variant="outlined" class="pa-4">
                                        <div class="text-subtitle-1 font-weight-bold mb-2">Notes</div>
                                        <div class="text-body-2" style="white-space: pre-wrap;">{{ driver.notes }}</div>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- ANALYTICS -->
                        <v-window-item value="analytics">
                            <v-alert v-if="!detail.score" type="info" variant="tonal" class="mb-3">
                                No safety score has been calculated for this driver yet.
                            </v-alert>

                            <template v-else>
                                <!-- Event stat cards -->
                                <v-row class="mb-1">
                                    <v-col v-for="stat in eventStats" :key="stat.label" cols="6" sm="4" md="3">
                                        <v-card rounded="lg" variant="tonal" :color="stat.color" class="pa-3">
                                            <div class="text-caption text-grey-darken-1">{{ stat.label }}</div>
                                            <div class="text-h6 font-weight-bold">{{ stat.value }}</div>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <!-- Component breakdown -->
                                    <v-col cols="12" md="6">
                                        <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                            <div class="text-subtitle-1 font-weight-bold mb-3">Score breakdown</div>
                                            <div v-for="c in components" :key="c.key" class="mb-3">
                                                <div class="d-flex justify-space-between text-body-2 mb-1">
                                                    <span>{{ c.label }}</span>
                                                    <span class="font-weight-medium">{{ c.value != null ? Number(c.value).toFixed(1) : '--' }}</span>
                                                </div>
                                                <v-progress-linear :model-value="(Number(c.value) || 0) * 10"
                                                                   :color="scoreColor(c.value)" height="8" rounded/>
                                            </div>
                                            <div class="text-caption text-grey mt-2">
                                                Period {{ fmtDate(detail.score.period_start) }} to {{ fmtDate(detail.score.period_end) }}
                                                <span v-if="detail.score.is_provisional"> &middot; provisional</span>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <!-- Trend -->
                                    <v-col cols="12" md="6">
                                        <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                            <div class="text-subtitle-1 font-weight-bold mb-3">Score trend</div>
                                            <Line v-if="trendData" :data="trendData" :options="trendOptions"
                                                  style="max-height: 240px"/>
                                            <div v-else class="text-body-2 text-grey">Not enough history to plot a trend yet.</div>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-window-item>

                        <!-- COMPLIANCE -->
                        <v-window-item value="compliance">
                            <div v-if="complianceLoading" class="pa-6 text-center">
                                <v-progress-circular indeterminate color="primary"/>
                            </div>
                            <template v-else>
                                <!-- Qualification banner -->
                                <v-alert v-if="qualification" :type="qualAlert.type" variant="tonal" class="mb-3"
                                         :icon="qualAlert.icon">
                                    <div class="font-weight-bold">{{ qualAlert.title }}</div>
                                    <div v-if="qualAlert.detail" class="text-body-2">{{ qualAlert.detail }}</div>
                                </v-alert>

                                <v-row>
                                    <!-- DQF checklist -->
                                    <v-col cols="12" md="4">
                                        <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                            <div class="text-subtitle-1 font-weight-bold mb-2">Qualification file</div>
                                            <div class="text-caption text-grey mb-3">Required FMCSA items</div>
                                            <div v-for="item in dqf" :key="item.type"
                                                 class="d-flex align-center ga-2 py-1">
                                                <v-icon :color="dqfColor(item.status)" size="20">
                                                    {{ dqfIcon(item.status) }}
                                                </v-icon>
                                                <span class="text-body-2">{{ item.label }}</span>
                                                <v-chip :color="statusMeta(item.status).color" size="x-small"
                                                        variant="tonal" class="ml-auto">
                                                    {{ statusMeta(item.status).label }}
                                                </v-chip>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <!-- Credentials list -->
                                    <v-col cols="12" md="8">
                                        <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                                            <div class="d-flex justify-space-between align-center mb-3">
                                                <div class="text-subtitle-1 font-weight-bold">Credentials &amp; documents</div>
                                                <v-btn color="primary" size="small" prepend-icon="mdi-plus"
                                                       @click="openCredentialDialog()">Add</v-btn>
                                            </div>

                                            <div v-if="!credentials.length" class="text-body-2 text-grey py-4 text-center">
                                                No credentials on file yet.
                                            </div>

                                            <v-list v-else density="compact">
                                                <v-list-item v-for="c in credentials" :key="c.id" class="px-0">
                                                    <template #prepend>
                                                        <v-icon :color="statusMeta(c.status).color">
                                                            {{ c.required ? 'mdi-star-circle' : 'mdi-file-document-outline' }}
                                                        </v-icon>
                                                    </template>
                                                    <v-list-item-title class="d-flex align-center ga-2">
                                                        {{ c.type_label }}
                                                        <span v-if="c.identifier" class="text-caption text-grey">
                                                            &middot; {{ c.identifier }}</span>
                                                        <v-chip :color="statusMeta(c.status).color" size="x-small"
                                                                variant="flat">{{ statusMeta(c.status).label }}</v-chip>
                                                    </v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        <span v-if="c.expires_on">
                                                            Expires {{ fmtDate(c.expires_on) }}
                                                            <span v-if="c.days_to_expiry != null && c.days_to_expiry >= 0">
                                                                ({{ c.days_to_expiry }}d)</span>
                                                        </span>
                                                        <span v-else>No expiry</span>
                                                        <span v-if="c.title"> &middot; {{ c.title }}</span>
                                                    </v-list-item-subtitle>
                                                    <template #append>
                                                        <v-btn v-if="c.has_document" icon="mdi-download" size="x-small"
                                                               variant="text" @click="downloadDocument(c)"/>
                                                        <v-btn icon="mdi-pencil" size="x-small" variant="text"
                                                               @click="openCredentialDialog(c)"/>
                                                        <v-btn icon="mdi-delete" size="x-small" variant="text"
                                                               color="error" @click="deleteCredential(c)"/>
                                                    </template>
                                                </v-list-item>
                                            </v-list>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-window-item>

                        <!-- COACHING -->
                        <v-window-item value="coaching">
                            <div v-if="coachingLoading && !coachingLoaded" class="pa-6 text-center">
                                <v-progress-circular indeterminate color="primary"/>
                            </div>
                            <v-row v-else>
                                <!-- Coaching sessions: one entry per session, a bulk review included -->
                                <v-col cols="12" md="7">
                                    <v-card rounded="lg" variant="outlined" class="pa-4" :loading="coachingLoading">
                                        <div class="d-flex justify-space-between align-center mb-3">
                                            <div class="text-subtitle-1 font-weight-bold">
                                                Coaching notes
                                                <span v-if="coachingMeta.total" class="text-caption text-grey ml-1">
                                                    ({{ coachingMeta.total }})</span>
                                            </div>
                                            <v-btn color="primary" size="small" prepend-icon="mdi-plus"
                                                   @click="openCoachingDialog()">Add</v-btn>
                                        </div>

                                        <div v-if="!coachingSessions.length" class="text-body-2 text-grey py-4 text-center">
                                            No coaching notes yet.
                                        </div>

                                        <div v-else class="coaching-list">
                                            <div v-for="s in coachingSessions" :key="s.id" class="coaching-entry"
                                                 :style="{borderLeftColor: coachingBorder(s.severity)}">
                                                <div class="d-flex justify-space-between align-start ga-2">
                                                    <div class="flex-grow-1" style="min-width: 0;">
                                                        <div class="d-flex align-center flex-wrap ga-2">
                                                            <span class="font-weight-medium">{{ s.topic }}</span>
                                                            <v-chip v-if="s.severity" size="x-small" variant="tonal"
                                                                    :color="severityColor(s.severity)">{{ s.severity }}</v-chip>
                                                            <v-chip size="x-small"
                                                                    :color="s.status === 'acknowledged' ? 'success' : 'grey'"
                                                                    variant="tonal">{{ s.status }}</v-chip>
                                                        </div>
                                                        <div v-if="s.notes" class="text-body-2 mt-1" style="white-space: pre-wrap; overflow-wrap: anywhere;">
                                                            {{ s.notes }}</div>
                                                        <div class="text-caption text-grey mt-1">
                                                            {{ s.coached_by || 'Unknown' }} &middot;
                                                            {{ s.coached_at ? formatDateTime(s.coached_at) : '' }}
                                                        </div>
                                                    </div>
                                                    <div class="d-flex ga-1 flex-shrink-0">
                                                        <v-btn v-if="s.status !== 'acknowledged'" icon="mdi-check"
                                                               size="x-small" variant="text" color="success"
                                                               aria-label="Mark acknowledged"
                                                               @click="acknowledgeCoaching(s)"/>
                                                        <v-btn icon="mdi-pencil" size="x-small" variant="text"
                                                               aria-label="Edit" @click="openCoachingDialog(s)"/>
                                                        <v-btn icon="mdi-delete" size="x-small" variant="text"
                                                               color="error" aria-label="Delete" @click="deleteCoaching(s)"/>
                                                    </div>
                                                </div>

                                                <template v-if="s.trips_count">
                                                    <v-btn variant="text" size="small" color="primary" class="px-1 mt-1"
                                                           :prepend-icon="expandedCoaching === s.id ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                                           @click="toggleCoachingTrips(s)">
                                                        Covers {{ s.trips_count }} {{ s.trips_count === 1 ? 'trip' : 'trips' }}
                                                    </v-btn>
                                                    <v-list v-if="expandedCoaching === s.id" density="compact" class="py-0 coaching-trips">
                                                        <template v-for="t in s.trips" :key="t.trip_id">
                                                            <v-list-item class="px-2">
                                                                <v-list-item-title class="text-body-2">
                                                                    {{ t.start_time ? formatDateTime(t.start_time) : fmtDate(t.trip_date) }}
                                                                    <span class="text-grey"> &middot; {{ t.vehicle || 'Unknown vehicle' }}</span>
                                                                </v-list-item-title>
                                                                <v-list-item-subtitle class="d-flex flex-wrap ga-1 mt-1">
                                                                    <v-chip v-if="t.speeding" size="x-small" color="error" variant="tonal">
                                                                        {{ t.speeding }} speeding</v-chip>
                                                                    <v-chip v-if="t.harsh_braking" size="x-small" color="warning" variant="tonal">
                                                                        {{ t.harsh_braking }} braking</v-chip>
                                                                    <v-chip v-if="t.hard_acceleration" size="x-small" color="warning" variant="tonal">
                                                                        {{ t.hard_acceleration }} accel</v-chip>
                                                                    <v-chip v-if="t.sharp_cornering" size="x-small" color="warning" variant="tonal">
                                                                        {{ t.sharp_cornering }} cornering</v-chip>
                                                                    <v-chip v-if="t.crash" size="x-small" color="error" variant="flat">
                                                                        {{ t.crash }} crash</v-chip>
                                                                    <span v-if="!t.total_events" class="text-caption">No events scored</span>
                                                                </v-list-item-subtitle>
                                                                <template #append>
                                                                    <v-btn v-if="t.asset_id" icon size="x-small" variant="text"
                                                                           :aria-label="coachingTripMap === `${s.id}-${t.trip_id}` ? 'Hide route' : 'Show route'"
                                                                           @click="toggleCoachingTripMap(s, t)">
                                                                        <v-icon size="18">{{ coachingTripMap === `${s.id}-${t.trip_id}` ? 'mdi-map-marker-off' : 'mdi-map-marker-path' }}</v-icon>
                                                                        <v-tooltip activator="parent" location="top">
                                                                            {{ coachingTripMap === `${s.id}-${t.trip_id}` ? 'Hide route' : 'Show route' }}</v-tooltip>
                                                                    </v-btn>
                                                                    <v-btn v-if="t.asset_id" icon size="x-small" variant="text"
                                                                           aria-label="Open the vehicle's trips" @click="openAssetTrips(t.asset_id)">
                                                                        <v-icon size="18">mdi-open-in-new</v-icon>
                                                                        <v-tooltip activator="parent" location="top">Open the vehicle's trips</v-tooltip>
                                                                    </v-btn>
                                                                </template>
                                                            </v-list-item>
                                                            <div v-if="coachingTripMap === `${s.id}-${t.trip_id}`" class="mb-2" style="height: 300px;">
                                                                <TripRouteMap :selected-device="tripDevice(t)" :selected-trip="{id: t.trip_id}"/>
                                                            </div>
                                                        </template>
                                                    </v-list>
                                                </template>
                                            </div>
                                        </div>

                                        <div v-if="coachingMeta.last_page > 1" class="d-flex justify-center pt-3">
                                            <v-pagination v-model="coachingPage" :length="coachingMeta.last_page" :total-visible="5"
                                                          density="compact" @update:model-value="fetchCoaching"/>
                                        </div>
                                    </v-card>
                                </v-col>

                                <!-- Flagged events -->
                                <v-col cols="12" md="5">
                                    <v-card rounded="lg" variant="outlined" class="pa-4">
                                        <div class="text-subtitle-1 font-weight-bold mb-1">Recent flagged trips</div>
                                        <div class="text-caption text-grey mb-3">Trips with harsh or speeding events</div>
                                        <div v-if="!flaggedEvents.length" class="text-body-2 text-grey py-2">
                                            No flagged trips.
                                        </div>
                                        <v-list v-else density="compact">
                                            <v-list-item v-for="e in flaggedEvents" :key="e.trip_id" class="px-0">
                                                <v-list-item-title class="text-body-2">
                                                    {{ fmtDate(e.trip_date) }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle class="d-flex flex-wrap ga-1 mt-1">
                                                    <v-chip v-if="e.speeding" size="x-small" color="error" variant="tonal">
                                                        {{ e.speeding }} speeding</v-chip>
                                                    <v-chip v-if="e.harsh_braking" size="x-small" color="warning" variant="tonal">
                                                        {{ e.harsh_braking }} braking</v-chip>
                                                    <v-chip v-if="e.hard_acceleration" size="x-small" color="warning" variant="tonal">
                                                        {{ e.hard_acceleration }} accel</v-chip>
                                                    <v-chip v-if="e.sharp_cornering" size="x-small" color="warning" variant="tonal">
                                                        {{ e.sharp_cornering }} cornering</v-chip>
                                                </v-list-item-subtitle>
                                                <template #append>
                                                    <v-btn icon="mdi-school" size="x-small" variant="text"
                                                           @click="openCoachingDialog(null, e.trip_id)">
                                                        <v-icon size="18">mdi-plus</v-icon>
                                                        <v-tooltip activator="parent" location="top">Coach from this trip</v-tooltip>
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- VEHICLES -->
                        <v-window-item value="vehicles">
                            <div v-if="vehiclesLoading" class="pa-6 text-center">
                                <v-progress-circular indeterminate color="primary"/>
                            </div>
                            <template v-else>
                                <v-card rounded="lg" variant="outlined" class="pa-4 mb-3">
                                    <div class="text-subtitle-1 font-weight-bold mb-2">Currently assigned</div>
                                    <div v-if="vehiclesCurrent.length" class="d-flex flex-wrap ga-2">
                                        <v-chip v-for="a in vehiclesCurrent" :key="a.id" color="primary" variant="tonal"
                                                prepend-icon="mdi-car" style="cursor: pointer;" @click="openAsset(a.id)">
                                            {{ a.label || ('Asset #' + a.id) }}
                                        </v-chip>
                                    </div>
                                    <div v-else class="text-body-2 text-grey">No vehicles currently assigned.</div>
                                </v-card>

                                <v-card rounded="lg" variant="outlined">
                                    <div class="text-subtitle-1 font-weight-bold pa-4 pb-2">Vehicles driven</div>
                                    <v-table v-if="vehiclesHistory.length" hover>
                                        <thead>
                                        <tr>
                                            <th class="text-left">Vehicle</th>
                                            <th class="text-left">Trips</th>
                                            <th class="text-left">Distance</th>
                                            <th class="text-left">First trip</th>
                                            <th class="text-left">Last trip</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="v in vehiclesHistory" :key="v.asset_id" style="cursor: pointer;"
                                            @click="openAsset(v.asset_id)">
                                            <td>{{ v.label || ('Asset #' + v.asset_id) }}</td>
                                            <td>{{ v.trip_count }}</td>
                                            <td>{{ unitifyDistance(v.total_km, 'distance', 'km') }}</td>
                                            <td>{{ fmtDate(v.first_trip) }}</td>
                                            <td>{{ fmtDate(v.last_trip) }}</td>
                                        </tr>
                                        </tbody>
                                    </v-table>
                                    <div v-else class="pa-6 text-center text-grey">No driving history yet.</div>
                                </v-card>
                            </template>
                        </v-window-item>

                        <!-- DOCUMENTS -->
                        <v-window-item value="documents">
                            <div v-if="documentsLoading" class="pa-6 text-center">
                                <v-progress-circular indeterminate color="primary"/>
                            </div>
                            <v-card v-else rounded="lg" variant="outlined">
                                <div class="d-flex justify-space-between align-center pa-4 pb-2">
                                    <div class="text-subtitle-1 font-weight-bold">Documents</div>
                                    <v-btn color="primary" size="small" prepend-icon="mdi-upload"
                                           @click="documentDialog = true">Upload</v-btn>
                                </div>
                                <div v-if="!documents.length" class="pa-6 text-center text-grey">
                                    No documents uploaded.
                                </div>
                                <v-list v-else density="compact">
                                    <v-list-item v-for="d in documents" :key="d.id" class="px-4">
                                        <template #prepend>
                                            <v-icon>mdi-file-document-outline</v-icon>
                                        </template>
                                        <v-list-item-title>
                                            {{ d.name }}
                                            <v-chip v-if="d.doc_type" size="x-small" variant="tonal" class="ml-1">
                                                {{ d.doc_type }}
                                            </v-chip>
                                        </v-list-item-title>
                                        <v-list-item-subtitle>
                                            <span v-if="d.file_name">{{ d.file_name }} &middot; </span>
                                            {{ d.uploaded_at ? formatDateTime(d.uploaded_at) : '' }}
                                            <span v-if="d.uploaded_by"> &middot; {{ d.uploaded_by }}</span>
                                            <span v-if="d.notes"> &middot; {{ d.notes }}</span>
                                        </v-list-item-subtitle>
                                        <template #append>
                                            <v-chip v-if="d.expires_on" size="x-small" variant="flat" class="mr-2"
                                                    :color="expiryColor(d.days_to_expiry)">
                                                {{ d.days_to_expiry < 0 ? 'Expired' : 'Expires' }} {{ fmtDate(d.expires_on) }}
                                            </v-chip>
                                            <v-btn icon="mdi-download" size="x-small" variant="text"
                                                   @click="downloadDoc(d)"/>
                                            <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                                   @click="deleteDoc(d)"/>
                                        </template>
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-window-item>

                        <!-- TRIPS -->
                        <v-window-item value="trips">
                            <v-card rounded="lg" variant="outlined">
                                <v-table v-if="trips.length" hover>
                                    <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-left">Date</th>
                                        <th class="text-left">Vehicle</th>
                                        <th class="text-left">Distance</th>
                                        <th class="text-left">Duration</th>
                                        <th class="text-left">Idle</th>
                                        <th class="text-left">Events</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <template v-for="trip in trips" :key="trip.id">
                                        <tr style="cursor: pointer;" @click="toggleTrip(trip)">
                                            <td>
                                                <v-icon size="small">
                                                    {{ expandedTrip === trip.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                                </v-icon>
                                            </td>
                                            <td>{{ formatDateTime(trip.time) }}</td>
                                            <td>{{ trip.asset_label || '--' }}</td>
                                            <td>{{ unitifyDistance(trip.distance, 'distance', 'km') }}</td>
                                            <td>{{ formatDuration(trip.duration) }}</td>
                                            <td>{{ trip.idle_percentage || '--' }}</td>
                                            <td>
                                                <span v-if="tripEventCount(trip)" class="d-flex align-center ga-1">
                                                    <v-icon size="14" color="warning">mdi-alert</v-icon>
                                                    {{ tripEventCount(trip) }}
                                                </span>
                                                <span v-else class="text-grey">0</span>
                                            </td>
                                        </tr>
                                        <tr v-if="expandedTrip === trip.id">
                                            <td colspan="7" class="pa-0">
                                                <div style="height: 360px;">
                                                    <TripRouteMap :selected-device="tripDevice(trip)"
                                                                  :selected-trip="trip"/>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    </tbody>
                                </v-table>

                                <div v-else-if="!tripsLoading" class="pa-6 text-center text-grey">
                                    No trips found for this driver.
                                </div>

                                <div v-if="tripsLoading" class="pa-6 text-center">
                                    <v-progress-circular indeterminate color="primary"/>
                                </div>

                                <div v-if="tripsTotalPages > 1" class="d-flex justify-center py-3">
                                    <v-pagination v-model="tripsPage" :length="tripsTotalPages" :total-visible="6"
                                                  density="compact" @update:model-value="fetchTrips"/>
                                </div>
                            </v-card>
                        </v-window-item>

                        <!-- SHIFTS -->
                        <v-window-item value="shifts">
                            <ShiftList v-if="shiftsOpened && detail" :driver-id="driverId" :company-id="driver.company_id" title="Shifts"
                                       :driver-name="fullName"
                                       @review-count="shiftReviewCount = $event"/>
                        </v-window-item>

                        <!-- MOBILE APPS -->
                        <v-window-item value="apps">
                            <DriverMobileApps v-if="appsOpened" ref="mobileAppsRef" :driver-id="driverId"
                                              :inviting="inviting" @invite="inviteToApp"/>
                        </v-window-item>

                        <!-- CHAT -->
                        <v-window-item value="chat">
                            <v-card rounded="lg" variant="outlined" class="pa-4">
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <h3 class="text-subtitle-1 font-weight-bold">Chat</h3>
                                    <v-btn size="small" variant="text" prepend-icon="mdi-bullhorn"
                                           @click="broadcastDialog = true">Broadcast to fleet</v-btn>
                                </div>
                                <div v-if="messagesLoading" class="text-center py-6">
                                    <v-progress-circular indeterminate color="primary"/>
                                </div>
                                <template v-else>
                                    <div v-if="!messages.length" class="text-body-2 text-grey py-4 text-center">
                                        No messages yet. Start the conversation below.
                                    </div>
                                    <div v-for="m in messagesOrdered" :key="m.id" class="d-flex mb-2"
                                         :class="m.direction === 'to_driver' ? 'justify-end' : 'justify-start'">
                                        <div class="pa-2 rounded" style="max-width: 70%;"
                                             :class="m.direction === 'to_driver' ? 'bg-primary text-white' : 'bg-grey-lighten-3'">
                                            <div class="text-body-2" style="white-space: pre-wrap;">{{ m.body }}</div>
                                            <div class="text-caption" style="opacity: 0.7;">
                                                {{ m.direction === 'to_driver' ? 'You' : 'Driver' }} · {{ fmtMsgDate(m.created_at) }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-flex ga-2 mt-3">
                                        <v-text-field v-model="messageDraft" placeholder="Message this driver…"
                                                      variant="outlined" density="compact" hide-details
                                                      @keyup.enter="sendMessage"/>
                                        <v-btn color="primary" :loading="messageSending"
                                               :disabled="!messageDraft.trim()" @click="sendMessage">Send</v-btn>
                                    </div>
                                </template>
                            </v-card>
                        </v-window-item>
                    </v-window>
                </v-col>
            </v-row>

            <!-- Broadcast to fleet dialog -->
            <v-dialog v-model="broadcastDialog" max-width="480">
                <v-card class="pa-4">
                    <h3 class="text-subtitle-1 font-weight-bold mb-1">Broadcast to all drivers</h3>
                    <p class="text-body-2 text-grey mb-3">Sends this message to every active driver in the company.</p>
                    <v-textarea v-model="broadcastBody" label="Message" variant="outlined" rows="3" counter maxlength="2000"/>
                    <div class="d-flex justify-end ga-2 mt-2">
                        <v-btn variant="text" @click="broadcastDialog = false">Cancel</v-btn>
                        <v-btn color="primary" :loading="broadcastSending" :disabled="!broadcastBody.trim()"
                               @click="sendBroadcast">Send to fleet</v-btn>
                    </div>
                </v-card>
            </v-dialog>
        </template>
    </v-container>

    <CredentialDialog v-if="credentialDialog" v-model="credentialDialog" :driver-id="driverId"
                      :credential="editingCredential" @saved="onCredentialSaved"/>

    <CoachingDialog v-if="coachingDialog" v-model="coachingDialog" :driver-id="driverId"
                    :session="editingCoaching" :trip-id="coachingTripId" @saved="onCoachingSaved"/>

    <EditDriver v-if="editDialog && editDriverPayload" v-model="editDialog" :driver="editDriverPayload"
                @updateDriver="onDriverUpdated"/>

    <DocumentDialog v-if="documentDialog" v-model="documentDialog" :driver-id="driverId"
                    @saved="fetchDocuments"/>
</template>

<script setup>
import {ref, computed, onMounted, provide, watch, h} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime, formatDateOnly} from "@/composables/useTimezone";
import {strOperations} from "@/composables/globals";
import UserAvatar from "@/components/UserAvatar.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import CredentialDialog from "@/components/dialogs/drivers/CredentialDialog.vue";
import CoachingDialog from "@/components/dialogs/drivers/CoachingDialog.vue";
import EditDriver from "@/components/dialogs/drivers/Edit.vue";
import DocumentDialog from "@/components/dialogs/drivers/DocumentDialog.vue";
import ShiftList from "@/components/drivers/shifts/ShiftList.vue";
import DriverMobileApps from "@/components/drivers/DriverMobileApps.vue";
import {Line} from "vue-chartjs";
import {Chart, registerables} from "chart.js";

Chart.register(...registerables);

// TripRouteMap reads live MQTT updates via inject; this page is not inside the
// Live view, so hand it inert stubs.
provide("latestLocations", ref(new Map()));
provide("lastMqttUpdate", ref(null));

// Tiny presentational helper for label/value rows in the overview cards.
const DetailRow = (props) => h("div", {class: "d-flex align-center ga-2 py-1"}, [
    h("i", {class: `mdi ${props.icon} text-grey-darken-1`, style: "font-size:18px"}),
    h("span", {class: "text-body-2 text-grey-darken-1", style: "min-width:110px"}, props.label),
    h("span", {class: "text-body-2 font-weight-medium"}, props.value || "--"),
]);
DetailRow.props = ["icon", "label", "value"];

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const {unitifyDistance} = strOperations();

// Calendar dates are formatted without timezone conversion (see formatDateOnly);
// show a dash when empty for table cells.
const fmtDate = (d) => (d ? formatDateOnly(d) : "--");

// Strip formatting so tel:/sms: links are valid dial strings (keep digits and +).
const cleanPhone = (p) => (p || "").replace(/[^\d+]/g, "");

// Document expiry chip colour: overdue = red, within 30 days = amber, else green.
const expiryColor = (days) => {
    if (days == null) return "grey";
    if (days < 0) return "error";
    if (days <= 30) return "warning";
    return "success";
};

const driverId = route.params.driver;
const loading = ref(true);
const detail = ref(null);
// Allow deep-linking straight to a tab (e.g. ?tab=coaching from the Safety
// rankings "Coach" action); fall back to overview for any unknown value.
const validTabs = ["overview", "analytics", "compliance", "coaching", "vehicles", "documents", "trips", "shifts", "apps", "chat"];
// "messages" was the Chat tab's old name; links to it still land there.
const requestedTab = route.query.tab === "messages" ? "chat" : route.query.tab;
const tab = ref(validTabs.includes(requestedTab) ? requestedTab : "overview");

// Shifts and Mobile apps load when first opened and stay mounted after, so
// switching tabs keeps an open shift and its map as they were.
const shiftsOpened = ref(tab.value === "shifts");
const appsOpened = ref(tab.value === "apps");
const shiftReviewCount = ref(0);
const mobileAppsRef = ref(null);

const driver = computed(() => detail.value?.driver ?? {});
const fullName = computed(() =>
    `${driver.value.user?.first_name || ""} ${driver.value.user?.last_name || ""}`.trim());

// The user accessor returns the placeholder avatar as a real URL; treat it as none.
const avatarSrc = computed(() => {
    const a = driver.value.user?.avatar;
    return a && !a.includes("avatar.png") ? a : null;
});

const licenseExpiringSoon = computed(() => {
    if (!driver.value.license_expiry) return false;
    const days = (new Date(driver.value.license_expiry) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
});

// Scores are on a 0-10 scale across the platform.
const scoreColor = (score) => {
    if (score == null) return "grey";
    if (score >= 8.0) return "success";
    if (score >= 5.0) return "warning";
    return "error";
};
const scoreBand = (score) => {
    if (score == null) return "No score";
    if (score >= 8.0) return "Excellent";
    if (score >= 5.0) return "Good";
    return "Needs improvement";
};

const components = computed(() => {
    const s = detail.value?.score;
    if (!s) return [];
    return [
        {key: "speeding", label: "Speeding", value: s.speeding_score},
        {key: "braking", label: "Braking", value: s.braking_score},
        {key: "acceleration", label: "Acceleration", value: s.acceleration_score},
        {key: "cornering", label: "Cornering", value: s.cornering_score},
        {key: "idling", label: "Idling", value: s.idling_score},
        {key: "discipline", label: "Discipline", value: s.discipline_score},
        {key: "crash", label: "Crash", value: s.crash_score},
    ].filter(c => c.value != null);
});

const eventStats = computed(() => {
    const e = detail.value?.event_totals;
    if (!e) return [];
    return [
        {label: "Trips", value: e.total_trips, color: "primary"},
        {label: "Miles", value: e.total_miles, color: "primary"},
        {label: "Speeding", value: e.speeding, color: "error"},
        {label: "Harsh braking", value: e.harsh_braking, color: "error"},
        {label: "Hard accel", value: e.hard_acceleration, color: "warning"},
        {label: "Sharp cornering", value: e.sharp_cornering, color: "warning"},
        {label: "Idle hours", value: e.total_idle_hours, color: "grey"},
        {label: "Night hours", value: e.total_night_hours, color: "grey"},
    ];
});

const trendData = computed(() => {
    const h = detail.value?.score_history;
    if (!h || h.length < 2) return null;
    return {
        labels: h.map(p => p.period_end),
        datasets: [{
            label: "Overall score",
            data: h.map(p => Number(p.overall_score)),
            borderColor: "#1976d2",
            backgroundColor: "rgba(25,118,210,0.1)",
            fill: true,
            tension: 0.3,
            pointRadius: 2,
        }],
    };
});
const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {y: {min: 0, max: 10, ticks: {stepSize: 2}}},
    plugins: {legend: {display: false}},
};

const formatDuration = (seconds) => {
    if (!seconds) return "--";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const tripEventCount = (trip) => {
    const a = trip.score_events || trip.driver_analytics;
    if (!a) return 0;
    // score_events has speeding_count; the driver_analytics fallback only has
    // speeding_seconds, so treat any speeding time as one event there.
    const speeding = a.speeding_count || (a.speeding_seconds > 0 ? 1 : 0);
    return (a.harsh_braking_count || 0) + (a.harsh_accel_count || 0)
        + (a.sharp_cornering_count || 0) + speeding;
};

const tripDevice = (trip) => ({
    asset_id: trip.asset_id,
    company: driver.value.company_id,
    slno: null,
});

// --- Trips tab ---
const trips = ref([]);
const tripsLoading = ref(false);
const tripsLoaded = ref(false);
const tripsPage = ref(1);
const tripsTotalPages = ref(1);
const expandedTrip = ref(null);

const toggleTrip = (trip) => {
    expandedTrip.value = expandedTrip.value === trip.id ? null : trip.id;
};

const fetchTrips = () => {
    tripsLoading.value = true;
    send("get", `drivers/${driverId}/trips?page=${tripsPage.value}`)
        .then((response) => {
            trips.value = response.data.data.data ?? [];
            tripsTotalPages.value = response.data.data.meta?.last_page
                ?? response.data.data.last_page ?? 1;
        })
        .catch(() => showSnackbar("Trips could not be loaded", "error"))
        .finally(() => {
            tripsLoading.value = false;
            tripsLoaded.value = true;
        });
};

// Lazy-load tab data the first time each tab is opened.
watch(tab, () => {
    if (tab.value === "trips" && !tripsLoaded.value) fetchTrips();
    if (tab.value === "compliance" && !complianceLoaded.value) fetchCompliance();
    if (tab.value === "coaching" && !coachingLoaded.value) fetchCoaching();
    if (tab.value === "chat" && !messagesLoaded.value) fetchMessages();
    if (tab.value === "vehicles" && !vehiclesLoaded.value) fetchVehicles();
    if (tab.value === "documents" && !documentsLoaded.value) fetchDocuments();
    if (tab.value === "shifts") shiftsOpened.value = true;
    if (tab.value === "apps") appsOpened.value = true;
});

// --- Documents tab ---
const documents = ref([]);
const documentsLoading = ref(false);
const documentsLoaded = ref(false);
const documentDialog = ref(false);

const fetchDocuments = () => {
    documentsLoading.value = true;
    send("get", `companies/drivers/${driverId}/documents`)
        .then((response) => {
            documents.value = response.data.data ?? [];
        })
        .catch(() => showSnackbar("Documents could not be loaded", "error"))
        .finally(() => {
            documentsLoading.value = false;
            documentsLoaded.value = true;
        });
};

const downloadDoc = (doc) => {
    send("get", `companies/drivers/${driverId}/documents/${doc.id}/download`, {}, {responseType: "blob"})
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = doc.file_name || doc.name || "document";
            link.click();
            window.URL.revokeObjectURL(url);
        })
        .catch(() => showSnackbar("Could not download document", "error"));
};

const deleteDoc = (doc) => {
    if (!confirm(`Delete "${doc.name}"?`)) return;
    send("delete", `companies/drivers/${driverId}/documents/${doc.id}`)
        .then((response) => {
            showSnackbar(response.data.message);
            fetchDocuments();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar("Could not delete document", "error"));
};

// --- Vehicles tab ---
const vehiclesCurrent = ref([]);
const vehiclesHistory = ref([]);
const vehiclesLoading = ref(false);
const vehiclesLoaded = ref(false);

const fetchVehicles = () => {
    vehiclesLoading.value = true;
    send("get", `drivers/${driverId}/vehicles`)
        .then((response) => {
            const d = response.data.data;
            vehiclesCurrent.value = d.current ?? [];
            vehiclesHistory.value = d.history ?? [];
        })
        .catch(() => showSnackbar("Vehicles could not be loaded", "error"))
        .finally(() => {
            vehiclesLoading.value = false;
            vehiclesLoaded.value = true;
        });
};

// --- Coaching tab ---
const coachingSessions = ref([]);
const flaggedEvents = ref([]);
const coachingLoading = ref(false);
const coachingLoaded = ref(false);
const coachingDialog = ref(false);
const editingCoaching = ref(null);
const coachingTripId = ref(null);
const coachingPage = ref(1);
const coachingMeta = ref({current_page: 1, last_page: 1, total: 0});
// The session whose "Covers N trips" list is open, and the trip whose route map is open in it.
const expandedCoaching = ref(null);
const coachingTripMap = ref(null);

const fetchCoaching = () => {
    coachingLoading.value = true;
    let refetching = false;
    send("get", `companies/drivers/${driverId}/coaching?page=${coachingPage.value}`)
        .then((response) => {
            const d = response.data.data;
            coachingSessions.value = d.sessions ?? [];
            coachingMeta.value = d.sessions_meta ?? {current_page: 1, last_page: 1, total: coachingSessions.value.length};
            flaggedEvents.value = d.flagged_events ?? [];
            // A delete can empty the last page; step back to the one that now ends the list.
            // That fetch owns the loading state from here.
            if (!coachingSessions.value.length && coachingPage.value > 1 && coachingMeta.value.last_page < coachingPage.value) {
                coachingPage.value = coachingMeta.value.last_page;
                refetching = true;
                fetchCoaching();
            }
        })
        .catch(() => showSnackbar("Coaching could not be loaded", "error"))
        .finally(() => {
            if (refetching) return;
            coachingLoading.value = false;
            coachingLoaded.value = true;
        });
};

const severityColor = (sev) => ({low: "info", medium: "warning", high: "error"}[sev] ?? "grey");
const coachingBorder = (sev) => {
    const color = {low: "info", medium: "warning", high: "error"}[sev];
    return color ? `rgb(var(--v-theme-${color}))` : "rgba(var(--v-border-color), var(--v-border-opacity))";
};

const toggleCoachingTrips = (session) => {
    expandedCoaching.value = expandedCoaching.value === session.id ? null : session.id;
    coachingTripMap.value = null;
};

const toggleCoachingTripMap = (session, trip) => {
    const key = `${session.id}-${trip.trip_id}`;
    coachingTripMap.value = coachingTripMap.value === key ? null : key;
};

// The vehicle's Trips tab (Asset details tab 5) in a new tab.
const openAssetTrips = (assetId) => window.open(`/assets/${assetId}?tab=5`, "_blank");

const openCoachingDialog = (session = null, tripId = null) => {
    editingCoaching.value = session;
    coachingTripId.value = tripId;
    coachingDialog.value = true;
};

const onCoachingSaved = () => {
    // A new session lands at the top of the first page.
    if (!editingCoaching.value) coachingPage.value = 1;
    fetchCoaching();
};

// --- Invite the driver to the self-service safety app ---
const inviting = ref(false);
const inviteToApp = () => {
    inviting.value = true;
    send("post", `companies/drivers/${driverId}/app-invite`)
        .then((r) => {
            showSnackbar(r.data?.message || "Driver invited to the SimpliGPS Driver app");
            // The Mobile apps tab shows the invite's state.
            mobileAppsRef.value?.refresh();
        })
        .catch((e) => showSnackbar(e?.response?.data?.message || "Could not send the invite", "error"))
        .finally(() => { inviting.value = false; });
};

// --- Chat tab ---
const messages = ref([]);
const messagesLoading = ref(false);
const messagesLoaded = ref(false);
const messageDraft = ref("");
const messageSending = ref(false);
const broadcastDialog = ref(false);
const broadcastBody = ref("");
const broadcastSending = ref(false);

const messagesOrdered = computed(() => [...messages.value].reverse());
const fmtMsgDate = (d) => d ? new Date(d).toLocaleString(undefined, {month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"}) : "";

const fetchMessages = () => {
    messagesLoading.value = true;
    send("get", `companies/drivers/${driverId}/messages`)
        .then((r) => { messages.value = r.data.data ?? []; })
        .catch(() => showSnackbar("Messages could not be loaded", "error"))
        .finally(() => { messagesLoading.value = false; messagesLoaded.value = true; });
};

const sendMessage = () => {
    const body = messageDraft.value.trim();
    if (!body) return;
    messageSending.value = true;
    send("post", `companies/drivers/${driverId}/messages`, {body})
        .then(() => { messageDraft.value = ""; fetchMessages(); })
        .catch(() => showSnackbar("Could not send", "error"))
        .finally(() => { messageSending.value = false; });
};

const sendBroadcast = () => {
    const body = broadcastBody.value.trim();
    if (!body) return;
    broadcastSending.value = true;
    send("post", "companies/driver-messages/broadcast", {body})
        .then((r) => {
            showSnackbar(`Sent to ${r.data.data?.sent ?? 0} drivers`);
            broadcastBody.value = "";
            broadcastDialog.value = false;
        })
        .catch(() => showSnackbar("Broadcast failed", "error"))
        .finally(() => { broadcastSending.value = false; });
};

const acknowledgeCoaching = (session) => {
    send("post", `companies/drivers/${driverId}/coaching/${session.id}/acknowledge`)
        .then((response) => {
            showSnackbar(response.data.message);
            fetchCoaching();
        })
        .catch(() => showSnackbar("Could not acknowledge", "error"));
};

const deleteCoaching = (session) => {
    if (!confirm("Delete this coaching note?")) return;
    send("delete", `companies/drivers/${driverId}/coaching/${session.id}`)
        .then((response) => {
            showSnackbar(response.data.message);
            fetchCoaching();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar("Could not delete", "error"));
};

// --- Compliance tab ---
const credentials = ref([]);
const qualification = ref(null);
const dqf = ref([]);
const complianceLoading = ref(false);
const complianceLoaded = ref(false);
const credentialDialog = ref(false);
const editingCredential = ref(null);

const fetchCompliance = () => {
    complianceLoading.value = true;
    send("get", `companies/drivers/${driverId}/credentials`)
        .then((response) => {
            const d = response.data.data;
            credentials.value = d.credentials ?? [];
            qualification.value = d.qualification ?? null;
            dqf.value = d.dqf ?? [];
        })
        .catch(() => showSnackbar("Compliance records could not be loaded", "error"))
        .finally(() => {
            complianceLoading.value = false;
            complianceLoaded.value = true;
        });
};

const STATUS_META = {
    valid: {color: "success", label: "Valid"},
    no_expiry: {color: "success", label: "On file"},
    expiring_soon: {color: "warning", label: "Expiring soon"},
    expired: {color: "error", label: "Expired"},
    missing: {color: "grey", label: "Missing"},
};
const statusMeta = (status) => STATUS_META[status] ?? {color: "grey", label: status};
const dqfColor = (status) => statusMeta(status).color;
const dqfIcon = (status) => {
    if (status === "valid" || status === "no_expiry") return "mdi-check-circle";
    if (status === "expiring_soon") return "mdi-alert-circle";
    return "mdi-close-circle";
};

const qualAlert = computed(() => {
    const q = qualification.value;
    if (!q) return {type: "info", icon: "mdi-information", title: "", detail: ""};
    const labels = {cdl: "CDL", medical_card: "medical card", mvr: "MVR"};
    const name = (arr) => (arr || []).map((t) => labels[t] ?? t).join(", ");
    if (q.status === "none") {
        return {type: "info", icon: "mdi-shield-outline", title: "No credentials on file",
            detail: "Add the required documents (CDL, medical card, MVR) to track this driver's qualification."};
    }
    if (q.status === "eligible") {
        return {type: "success", icon: "mdi-shield-check", title: "Eligible",
            detail: "All required qualification items are on file and current."};
    }
    if (q.status === "at_risk") {
        return {type: "warning", icon: "mdi-shield-alert", title: "At risk",
            detail: `Expiring soon: ${name(q.expiring_soon)}.`};
    }
    const parts = [];
    if (q.missing?.length) parts.push(`missing ${name(q.missing)}`);
    if (q.expired?.length) parts.push(`expired ${name(q.expired)}`);
    return {type: "error", icon: "mdi-shield-off", title: "Ineligible",
        detail: parts.length ? `Required items ${parts.join(" and ")}.` : ""};
});

const openCredentialDialog = (credential = null) => {
    editingCredential.value = credential;
    credentialDialog.value = true;
};

const onCredentialSaved = () => fetchCompliance();

const deleteCredential = (credential) => {
    if (!confirm(`Delete this ${credential.type_label}?`)) return;
    send("delete", `companies/drivers/${driverId}/credentials/${credential.id}`)
        .then((response) => {
            showSnackbar(response.data.message);
            fetchCompliance();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar("Could not delete credential", "error"));
};

const downloadDocument = (credential) => {
    send("get", `companies/drivers/${driverId}/credentials/${credential.id}/download`, {}, {responseType: "blob"})
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = credential.document_name || "document";
            link.click();
            window.URL.revokeObjectURL(url);
        })
        .catch(() => showSnackbar("Could not download document", "error"));
};

const fetchDetail = () => {
    loading.value = true;
    send("get", `companies/drivers/${driverId}`)
        .then((response) => {
            detail.value = response.data.data;
            fetchShiftReviewCount();
        })
        .catch(() => showSnackbar("Driver could not be loaded", "error"))
        .finally(() => loading.value = false);
};

// The Shifts tab's badge, before the tab is opened, so a shift waiting for a
// manager draws the eye from the moment the page loads. Quiet on failure: a
// role that cannot see shifts simply gets no badge.
const fetchShiftReviewCount = () => {
    const params = {driver: driverId, needs_review: 1};
    if (detail.value?.driver?.company_id) params.company = detail.value.driver.company_id;
    send("get", "fleet/duty/sessions", params)
        .then((r) => {
            shiftReviewCount.value = r.data.data?.needs_review_count ?? 0;
        })
        .catch(() => {});
};

// Edit driver: reuse the list's edit dialog. It expects the flattened list-row
// shape, so adapt the nested detail.driver (user.* -> user_* keys).
const editDialog = ref(false);
const editDriverPayload = computed(() => {
    const d = detail.value?.driver;
    if (!d) return null;
    return {
        ...d,
        user_first_name: d.user?.first_name,
        user_last_name: d.user?.last_name,
        user_email: d.user?.email,
        user_phone: d.user?.contact_phone,
        user_mobile_phone: d.user?.mobile_phone,
        user_timezone: d.user?.timezone,
        user_avatar: avatarSrc.value,
    };
});
const onDriverUpdated = () => {
    editDialog.value = false;
    fetchDetail();
};

// Compliance tab indicator, coloured from the server roll-up.
const complianceTabColor = computed(() => {
    const s = detail.value?.compliance_status;
    if (s === "ineligible") return "error";
    if (s === "at_risk") return "warning";
    return undefined;
});
const complianceTabIcon = computed(() => {
    const s = detail.value?.compliance_status;
    if (s === "ineligible") return "mdi-shield-alert";
    if (s === "at_risk") return "mdi-shield-alert-outline";
    if (s === "none") return "mdi-shield-outline";
    return "mdi-shield-check";
});

// 30-day safety-score trend from the score history (latest vs ~30 days prior).
const scoreTrend = computed(() => {
    const h = detail.value?.score_history;
    if (!h || h.length < 2) return null;
    const latest = h[h.length - 1];
    // Parse and shift in UTC consistently so the 30-day boundary isn't skewed
    // by the local timezone offset.
    const toUtc = (d) => new Date(`${d}T00:00:00Z`);
    const target = toUtc(latest.period_end);
    target.setUTCDate(target.getUTCDate() - 30);
    let prior = h[0];
    for (const p of h) {
        if (toUtc(p.period_end) <= target) prior = p;
        else break;
    }
    const delta = Math.round((Number(latest.overall_score) - Number(prior.overall_score)) * 10) / 10;
    if (Math.abs(delta) < 0.1) return {icon: "mdi-trending-neutral", color: "grey", label: "flat"};
    return delta > 0
        ? {icon: "mdi-trending-up", color: "success", label: `+${delta}`}
        : {icon: "mdi-trending-down", color: "error", label: `${delta}`};
});

const goBack = () => router.push({name: "drivers"});
const openAsset = (assetId) => window.open(`/assets/${assetId}`, "_blank");

onMounted(() => {
    fetchDetail();

    // The tab watcher only fires on change, so a deep-link straight to a tab
    // (e.g. ?tab=coaching from the Safety inbox) needs its data loaded here.
    if (tab.value === "trips" && !tripsLoaded.value) fetchTrips();
    if (tab.value === "compliance" && !complianceLoaded.value) fetchCompliance();
    if (tab.value === "coaching" && !coachingLoaded.value) fetchCoaching();
    if (tab.value === "chat" && !messagesLoaded.value) fetchMessages();
    if (tab.value === "vehicles" && !vehiclesLoaded.value) fetchVehicles();
    if (tab.value === "documents" && !documentsLoaded.value) fetchDocuments();

    // ?trip= opens a new coaching session pre-filled for that trip (used by the
    // Safety inbox "Coach" action on not-yet-coached events).
    const tripId = Number(route.query.trip);
    if (tab.value === "coaching" && tripId > 0) {
        openCoachingDialog(null, tripId);
    }
});
</script>

<style scoped>
.h-100 {
    height: 100%;
}

/* .max-height fixes the page height on desktop; scroll inside it so long tabs are reachable. */
.driver-details {
    overflow-y: auto;
}

.coaching-entry {
    border-left: 3px solid;
    padding: 10px 0 10px 12px;
}

.coaching-entry + .coaching-entry {
    margin-top: 8px;
}

.coaching-trips {
    background: transparent;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

@media (min-width: 960px) {
    .coaching-list {
        max-height: 60vh;
        overflow-y: auto;
        padding-right: 4px;
    }
}
</style>
